/**
 * Call-Off ASN/Shipments Tab — lists Advanced Shipping Notices that
 * originated from this call-off (`source_type='CallOff'`).
 *
 * Data is fetched on-demand via `purchasingAPI.getASNs({ call_off })`.
 */
import { useEffect, useState } from 'react';
import { Loader2, Truck, Package, Calendar, ExternalLink } from 'lucide-react';
import { purchasingAPI } from '../../../lib/api';
import type { AdvancedShippingNotice } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';

interface CallOffASNTabProps {
  callOffId: number;
}

const STATUS_CLASSES: Record<string, string> = {
  created: 'bg-gray-100 text-gray-700',
  submitted: 'bg-blue-100 text-blue-700',
  in_transit: 'bg-orange-100 text-orange-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  received: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function CallOffASNTab({ callOffId }: CallOffASNTabProps) {
  const { formatDate } = useFormatters();
  const [asns, setASNs] = useState<AdvancedShippingNotice[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    purchasingAPI.getASNs({ call_off: callOffId, page_size: 50 })
      .then((res) => { if (!cancelled) setASNs(res.data.results || []); })
      .catch(() => { /* non-critical */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [callOffId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (asns.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Truck className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No shipments raised against this call-off yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {asns.map((asn) => {
        const statusClass = STATUS_CLASSES[asn.status] || STATUS_CLASSES['created'];
        return (
          <div key={asn.id} className="border rounded-lg p-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Truck className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={`/purchasing?tab=asn&asn=${asn.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono font-semibold text-primary hover:underline flex items-center gap-1"
                  >
                    {asn.asn_number}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                    {asn.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
                  {asn.courier && (
                    <div>
                      <span className="text-xs text-muted-foreground block">Courier</span>
                      <span className="font-medium">{asn.courier}</span>
                    </div>
                  )}
                  {asn.tracking_number && (
                    <div>
                      <span className="text-xs text-muted-foreground block">Tracking</span>
                      <span className="font-medium font-mono text-xs">{asn.tracking_number}</span>
                    </div>
                  )}
                  {asn.shipment_date && (
                    <div>
                      <span className="text-xs text-muted-foreground block flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Shipped
                      </span>
                      <span className="font-medium">{formatDate(asn.shipment_date)}</span>
                    </div>
                  )}
                  {asn.estimated_delivery_date && (
                    <div>
                      <span className="text-xs text-muted-foreground block flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        ETA
                      </span>
                      <span className="font-medium">{formatDate(asn.estimated_delivery_date)}</span>
                    </div>
                  )}
                  {asn.actual_delivery_date && (
                    <div>
                      <span className="text-xs text-muted-foreground block flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Delivered
                      </span>
                      <span className="font-medium text-emerald-700">{formatDate(asn.actual_delivery_date)}</span>
                    </div>
                  )}
                </div>
                {asn.delivery_lines_details && asn.delivery_lines_details.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center gap-1 mb-2">
                      <Package className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">
                        Items shipped ({asn.delivery_lines_details.length})
                      </span>
                    </div>
                    <div className="space-y-1">
                      {asn.delivery_lines_details.map((dl) => (
                        <div key={dl.id} className="text-xs flex items-center gap-2">
                          <span className="font-mono">{dl.part_number}</span>
                          <span className="text-muted-foreground truncate">{dl.part_description}</span>
                          <span className="ml-auto font-medium">{dl.qty_released}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
